import _ from 'lodash'
import { useMemo } from 'react'

import { IDataType as IGroupType, useGroupFilter } from '../../groupFilter'
import {
  IColoredText,
  ILinkPropsType,
  ITogglePropsType,
} from '../../table/Format'
import { useVariables } from '../../variables'
import useApi, { IApiKeyType, configs } from './useApi'

type IFormatType =
  | {
      type: 'price'
      unit?: 'dollar' | 'cent'
    }
  | {
      type: 'percent'
      decimal?: number
    }
  | {
      type: 'number'
      decimal?: number
    }
  | { type: 'string' }
  | { type: 'link' }
  | { type: 'boolean' }
  | { type: 'stars' }
  | { type: 'button' }
  | { type: 'date' }
  // FIXME: Need to remove all types below
  | { type: 'toggle' }
  | { type: 'delete-development-plan' }
  | { type: 'feedback-name' }
  | { type: 'timeInPosition' }
  | { type: 'date-diff' }
  | { type: 'feedback' }
  | { type: 'date-string' }
  | { type: 'completionRate' }
  | { type: 'progress-bar' }
  | { type: 'delete-training-tracker' }
  | { type: 'colored-text' }
  | { type: 'tracker-name' }
  | { type: 'tracker' }
  | { type: 'tracker-completion-rate' }
  | { type: 'second' }
  | { type: 'basisPoint' }

type IFeildType<V extends Record<string, Pick<IFormatType, 'type'>['type']>> = {
  [K in keyof V]: Omit<
    Extract<
      IFormatType,
      {
        type: V[K]
      }
    >,
    'type'
  > & {
    key: K
    title: string
    hide?: string
  }
}

type IFeildsType = {
  [K in keyof typeof configs]: IFeildType<typeof configs[K]>
}

export interface ICsvDataArgsType<K extends IApiKeyType> {
  api: K
  sortedBy?: keyof typeof configs[K] | 'groupInfo'
  fields: (
    | IFeildsType[K][keyof IFeildsType[K]]
    | 'groupInfo'
    | { key: `<%- ${string} %>`; hide: `<%- ${string} %>` }
  )[]
}

type IGroupInfoType = NonNullable<IGroupType['list']>[number]

const useCsvData = <K extends IApiKeyType>(
  { api, sortedBy, fields: originalFields }: ICsvDataArgsType<K>,
  data?: ReturnType<typeof useApi>['data'],
) => {
  const { groupFilter } = useGroupFilter()
  const dataSource = useMemo((): NonNullable<typeof data>['source'] | null => {
    if (!data?.source) return null

    const groupInfo = groupFilter?.list?.[0]

    if (!data.summary || !groupInfo) return data.source

    if (groupInfo.groupBy)
      return [
        ...data.source,
        {
          ...data.summary,
          groupInfo: {
            ...groupInfo,
            name: 'Total',
            groupBy: {
              ...groupInfo.groupBy,
              name: ' ',
            },
          },
        },
      ]

    return [
      ...data.source,
      {
        ...data.summary,
        groupInfo: {
          ...groupInfo,
          name: 'Total',
        },
      },
    ]
  }, [data, groupFilter])
  const { variables, template } = useVariables()
  const fields = useMemo(
    () =>
      originalFields
        .map((field) => {
          if (field === 'groupInfo') return field

          if (field.hide && template(field.hide) === 'true') return []

          if (typeof field.key === 'string') {
            const newFields = template(field.key)

            if (newFields instanceof Array) {
              return newFields.reduce((acc, f) => {
                if ('children' in f) {
                  for (const child of f.children) {
                    const col = {
                      ...child,
                      title: `${f.title} - ${child.title}`,
                    }

                    acc.push(col)
                  }

                  return acc
                }

                const col = {
                  ...field,
                  ...f,
                  originalFieldKey: field.key,
                }

                return [...acc, col]
              }, [])
            }
          }

          return field
        })
        .flat(),
    [originalFields, template],
  )

  const csvData = useMemo(() => {
    if (!dataSource) return []

    try {
      const headers = fields
        .filter((field) => {
          if (field === 'groupInfo') {
            return true
          }

          if (!field.hide) return true

          return _.template(field.hide)(variables) !== 'true'
        })
        .map((field) => {
          if (field === 'groupInfo') {
            if (dataSource[0] && 'groupInfo' in dataSource[0]) {
              const groupInfo = dataSource[0].groupInfo as IGroupInfoType
              return [groupInfo.header, groupInfo.groupBy?.header].filter(
                Boolean,
              )
            }

            return []
          }

          return (() => {
            try {
              return _.template(field.title)(variables) || ' '
            } catch (e) {
              return ' '
            }
          })()
        })
        .flat()
      const rows = (
        !sortedBy
          ? dataSource
          : dataSource.sort((a, b) => {
              const aValue = a[sortedBy as keyof typeof a]
              const bValue = b[sortedBy as keyof typeof b]

              if (sortedBy === 'groupInfo')
                return (aValue as IGroupInfoType).name.localeCompare(
                  (bValue as IGroupInfoType).name,
                )

              const config = configs[api][sortedBy]

              switch (config) {
                case 'string':
                  return (aValue as string).localeCompare(bValue as string)
                default:
                  return (aValue as number) - (bValue as number)
              }
            })
      ).map((d) =>
        fields
          .filter((field) => {
            if (field === 'groupInfo') {
              return true
            }

            if (!field.hide) return true

            return _.template(field.hide)(variables) !== 'true'
          })
          .map(
            (
              field: ICsvDataArgsType<K>['fields'][number] & {
                originalFieldKey?: string
              },
            ) => {
              if (field === 'groupInfo') {
                if (d && 'groupInfo' in d) {
                  const groupInfo = d.groupInfo as IGroupInfoType
                  if ('groupBy' in groupInfo) {
                    return [groupInfo.name, groupInfo.groupBy?.name]
                  }

                  return [groupInfo.name]
                }

                return []
              }

              const value = _.get(d, field.key)
              const config =
                configs[api][
                  (field.originalFieldKey ||
                    field.key) as keyof typeof configs[K]
                ]

              if (value === null || value === undefined) return ''

              switch (config) {
                case 'price': {
                  const unit = (
                    field as unknown as Extract<IFormatType, { type: 'price' }>
                  ).unit

                  if (unit === 'dollar')
                    return parseFloat(value as string).toFixed(2)

                  return (parseFloat(value as string) / 100).toFixed(2)
                }
                case 'percent':
                case 'number':
                  const decimal = (
                    field as unknown as Extract<
                      IFormatType,
                      { type: 'percent' | 'number' }
                    >
                  ).decimal

                  return parseFloat(value as string).toFixed(decimal || 2)
                case 'link':
                  return (value as ILinkPropsType).label
                case 'toggle':
                  const toggleValue = value as ITogglePropsType
                  if (toggleValue.label === undefined)
                    return toggleValue.checked

                  return toggleValue.label
                case 'colored-text':
                  return (value as IColoredText).text
                default:
                  return value
              }
            },
          )
          .flat(),
      )

      return [headers, ...rows]
    } catch (e) {
      console.error(e)

      return []
    }
  }, [dataSource, api, sortedBy, fields, variables])

  return csvData
}

export default useCsvData
